import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AuthLayout } from "@/components/AuthLayout";
import { VerifyCodeStep } from "@/components/VerifyCodeStep";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MfaVerify, MfaSetup } from "@/components/MfaStep";

export default function Login() {
  const [params] = useSearchParams();
  const next = params.get("next") || "/app";
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [otp, setOtp] = useState(null);
  const [mfa, setMfa] = useState(null);

  const done = (data) => {
    if (data.mfa_required || data.mfa_setup_required) { setOtp(null); setMfa(data); return; }
    setUser(data.user);
    nav(next, { replace: true });
  };

  const passwordLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.otp_required) setOtp({ endpoint: "/auth/verify-email", resend: "/auth/login", body: { email, password } });
      else done(data);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const codeLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/otp/request", { email });
      setOtp({ endpoint: "/auth/otp/verify", resend: "/auth/otp/request", body: { email } });
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const footer = <>New to PostalCloud? <Link data-testid="goto-register-link" to={`/register?next=${encodeURIComponent(next)}`} className="underline font-medium text-slate-900 dark:text-white hover:text-emerald-600">Create an account</Link></>;

  if (mfa) {
    return (
      <AuthLayout title={mfa.mfa_required ? "Two-step sign-in" : "Set up two-step sign-in"} subtitle={mfa.mfa_required ? "One more step to protect your account" : "Required for admins and organisation Owners"} footer={footer}>
        {mfa.mfa_required ? <MfaVerify mfaToken={mfa.mfa_token} onDone={done} /> : <MfaSetup mfaToken={mfa.mfa_token} onDone={done} />}
      </AuthLayout>
    );
  }

  if (otp) {
    return (
      <AuthLayout title="Enter your code" subtitle="One-time code access" footer={footer}>
        <VerifyCodeStep email={email.toLowerCase()} endpoint={otp.endpoint} onSuccess={done} resendEndpoint={otp.resend} resendBody={otp.body} />
      </AuthLayout>
    );
  }

  const emailField = (
    <div className="space-y-2">
      <Label htmlFor="email">Email</Label>
      <Input id="email" data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" />
    </div>
  );

  return (
    <AuthLayout title="Sign in" subtitle="Access your verified mailbox." footer={footer}>
      <Tabs defaultValue="password">
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger data-testid="login-tab-password" value="password">Password</TabsTrigger>
          <TabsTrigger data-testid="login-tab-code" value="code">Email code</TabsTrigger>
        </TabsList>
        <TabsContent value="password">
          <form onSubmit={passwordLogin} className="space-y-4 mt-6">
            {emailField}
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
            <Button data-testid="login-submit-btn" type="submit" disabled={busy} className="w-full h-11 rounded-full">{busy && <Loader2 className="animate-spin mr-2" size={16} />}Sign in</Button>
          </form>
        </TabsContent>
        <TabsContent value="code">
          <form onSubmit={codeLogin} className="space-y-4 mt-6">
            {emailField}
            {error && <p data-testid="login-code-error" className="text-sm text-red-600">{error}</p>}
            <Button data-testid="login-send-code-btn" type="submit" disabled={busy} className="w-full h-11 rounded-full">{busy && <Loader2 className="animate-spin mr-2" size={16} />}Email me a code</Button>
          </form>
        </TabsContent>
      </Tabs>
    </AuthLayout>
  );
}
